import React, { useState } from 'react';
import { Card, Spinner, EmptyState, AlertCircle, Button, ChevronLeft, ChevronRight } from '@opendate-app/setlist';

function abbreviateCount(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
  return `${n}`;
}

const rowButtonReset = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
};

function BarRow({ row, maxCount, onClick, href }) {
  const drillable = Boolean(onClick || href);
  const content = (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 14, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
          {row.name}
          {drillable && <ChevronRight width={16} height={16} aria-hidden="true" style={{ color: 'var(--sl-text-quaternary, #717680)' }} />}
        </span>
        <span style={{ color: 'var(--sl-text-quaternary, #717680)', whiteSpace: 'nowrap' }}>{abbreviateCount(row.count)} · {row.pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: 'var(--sl-bg-secondary, #f2f4f7)' }}>
        <div
          style={{
            height: '100%',
            borderRadius: 3,
            background: 'var(--sl-fg-brand)',
            minWidth: 2,
            transition: 'width 400ms ease',
            width: `${maxCount === 0 ? 0 : (row.count / maxCount) * 100}%`,
          }}
        />
      </div>
    </>
  );

  if (!drillable) return <div style={{ padding: '8px 0' }}>{content}</div>;

  const rowStyle = { ...rowButtonReset, background: undefined, padding: '8px', margin: '0 -8px', width: 'calc(100% + 16px)', borderRadius: 8 };

  if (href) {
    return (
      <a href={href} className="event-types-card-row text-reset text-decoration-none" style={rowStyle}>
        {content}
      </a>
    );
  }

  return (
    <button type="button" onClick={onClick} className="event-types-card-row" style={rowStyle}>
      {content}
    </button>
  );
}

export default function EventTypesCard({ insights, insightsError, canViewSegments, contactLabel = 'Fans' }) {
  const [selectedName, setSelectedName] = useState(null);
  const eventTypes = insights && insights.event_types ? insights.event_types : [];
  const selected = eventTypes.find((row) => row.name === selectedName);
  const rows = selected ? selected.genres : eventTypes;
  const maxCount = rows.reduce((max, row) => Math.max(max, row.count), 0);

  return (
    <Card padding="md">
      <style>{'.event-types-card-row { background: none; } .event-types-card-row:hover { background: var(--sl-bg-primary-hover, #f5f5f6); }'}</style>
      {selected ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Button variant="secondary" size="sm" icon={ChevronLeft} aria-label="Back to all event types" onClick={() => setSelectedName(null)} />
            <h2 style={{ margin: 0, fontSize: 18, display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <button type="button" onClick={() => setSelectedName(null)} style={{ ...rowButtonReset, width: 'auto', padding: 0, color: 'var(--sl-text-tertiary, #535862)' }}>
                What they show up for
              </button>
              <span aria-hidden="true" style={{ color: 'var(--sl-text-quaternary, #717680)' }}>/</span>
              <span style={{ fontWeight: 600 }}>{selected.name}</span>
            </h2>
          </div>
          <div style={{ fontSize: 13, color: 'var(--sl-text-quaternary, #717680)', marginBottom: 8 }}>
            {abbreviateCount(selected.count)} {contactLabel.toLowerCase()} · {selected.pct}% of all {contactLabel.toLowerCase()} · by genre
          </div>
        </>
      ) : (
        <h2 style={{ margin: '0 0 12px', fontSize: 18 }}>What they show up for</h2>
      )}
      {insightsError ? (
        <EmptyState
          size="sm"
          icon={AlertCircle}
          title="Couldn't load event types"
          description="Refresh the page to try again."
        />
      ) : !insights ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <Spinner label="Loading…" />
        </div>
      ) : eventTypes.length === 0 ? (
        <EmptyState size="sm" title="No event type data yet" />
      ) : (
        rows.map((row) => (
          <BarRow
            key={row.name}
            row={row}
            maxCount={maxCount}
            onClick={!selected && row.genres && row.genres.length > 0 ? () => setSelectedName(row.name) : null}
            href={selected && canViewSegments ? row.segment_url : null}
          />
        ))
      )}
    </Card>
  );
}
